// Single source of truth for which nest `slug` strings have a
// captain-tasko-native React page. Peer to native-tool-types.ts.
// Used by:
//   - NestsPanel: surfaces an "Open" button on matched rows
//   - AppShell (sidebar): links a Nest cell directly to the native
//     page when a match exists
//
// Add a row here AND register the route in apps/web/src/router.tsx
// when shipping a new native Nest UI.
//
// 0.1.53 — External Clients Nest lands as the first entry per
// CTSK-166 Phase B. The pattern mirrors NATIVE_TOOL_TYPES so future
// meta-Nests in Mission Control (or any org) can slot in without
// changing the sidebar / NestsPanel wiring.

// CTSK-357 — canonical ECN slug constant. The slug is a web-side
// concept today (zero hits in packages/shared), so it lives here next
// to the map that consumes it rather than in shared (keeps the ship
// 3-way lockstep). Promote to shared if the API ever needs it.
export const EXTERNAL_CLIENTS_NEST_SLUG = 'external-clients';

export const NATIVE_NEST_TYPES: Record<string, string> = {
  [EXTERNAL_CLIENTS_NEST_SLUG]: '/mission-control/nests/external-clients',
};

/** CTSK-357 — single source of truth for where a Mission Control /
 *  sidebar nest tile's name-row click lands.
 *
 *  CTSK-384 — a bundle-declared `nest.landingPath` (persisted to
 *  `nests.landing_path` by the bundle importer, surfaced via
 *  /stats/nests) now takes precedence over the hardcoded routing.
 *  Mirrors CTSK-347's tool.manifest.landingPath. Three shapes:
 *
 *  - `/relative/route` or absolute `http(s)://…` → wins outright
 *    (CTSK-347-identical semantics).
 *  - `?query=string` (e.g. `?tab=pending`) → composed onto the nest's
 *    base path: appended verbatim to a native page, merged with `&`
 *    onto the already-query-bearing documents-filter fallback.
 *  - unset / invalid shape → hardcoded fallback below (invalid values
 *    are rejected at import by BundleNestBlockSchema; the guard here
 *    covers stale rows + hand-edited DBs without throwing).
 *
 *  Hardcoded fallback (unchanged from CTSK-357):
 *  - External Clients Nest → its native page with `?tab=pending` (the
 *    operator-preferred landing tab: the queue that needs action;
 *    ExternalClientsNest consumes `?tab=` since CTSK-357 — deep-links
 *    to any tab work). Changed from `?tab=all` on 2026-08-17 per
 *    dr.the0 — pending is what the operator opens ECN to work on.
 *  - Other native nests (future) → their registered native page.
 *  - Everything else → the generic documents-by-nest filter view.
 *
 *  Kept as a pure helper so the routing conditional is unit-testable
 *  without rendering AppShell's session-heavy tree. */
export function nestTileDocsPath(slug: string, landingPath?: string | null): string {
  const nativeNestPath = NATIVE_NEST_TYPES[slug];
  const fallback =
    slug === EXTERNAL_CLIENTS_NEST_SLUG && nativeNestPath
      ? `${nativeNestPath}?tab=pending`
      : nativeNestPath ?? `/documents?nest=${encodeURIComponent(slug)}`;
  if (typeof landingPath === 'string' && landingPath.length > 0) {
    // Full-path / absolute shapes win outright (CTSK-347 semantics).
    if (landingPath.startsWith('/') || /^https?:\/\//.test(landingPath)) {
      return landingPath;
    }
    // Query-string shape composes onto the nest's base path.
    if (landingPath.startsWith('?')) {
      const base = nativeNestPath ?? `/documents?nest=${encodeURIComponent(slug)}`;
      return base.includes('?')
        ? `${base}&${landingPath.slice(1)}`
        : `${base}${landingPath}`;
    }
    // Unrecognized shape → same fallback, warn instead of throwing
    // (ticket AC7): the importer validates, so this only fires on
    // stale/hand-edited rows.
    console.warn(
      `nestTileDocsPath: ignoring invalid landingPath ${JSON.stringify(landingPath)} for nest "${slug}"`,
    );
  }
  return fallback;
}
